// Where keyboard focus goes when a change to a row moves the row out from under it (04-data §1.10.2 rule 4: "focus
// returns to the cell so a retry costs one keystroke"; 01-foundations §7.2; Phase 1c finding D25). A to-do ticked in a
// list of open to-dos leaves that list when the list is read again, and the browser drops the focus its box held to the
// document — a keyboard reader who ticked is sent back to the page's start. Instead focus goes to the same control in the
// row now standing where that one stood, so the next to-do is one keystroke away, as the ticked one was.

/** A row's place when a change to it began: its id and its index among the rows shown, in their order. */
export interface RowPlace {
  id: string;
  index: number;
}

/** What every row of a `DataTable` carries, as a table row or a card: its key. */
const ROW_SELECTOR = '[data-row-id]';

/**
 * The row whose control takes focus once the change has settled (pure, pinned under node): the same row while it is
 * still shown; otherwise the row now at its place — the one after it, moved up — or the last row when it was the last;
 * null when no row is left.
 */
export function rowFocusSuccessor(ids: readonly string[], place: RowPlace): string | null {
  if (ids.includes(place.id)) return place.id;
  if (ids.length === 0) return null;
  return ids[Math.min(Math.max(place.index, 0), ids.length - 1)] ?? null;
}

function shownRows(root: ParentNode): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(ROW_SELECTOR));
}

/** The place of the row keyed `id` among the rows shown under `root`, or null when it is not shown. */
export function rowPlace(root: ParentNode, id: string): RowPlace | null {
  const index = shownRows(root).findIndex((row) => row.dataset.rowId === id);
  return index < 0 ? null : { id, index };
}

/**
 * After the change has settled and the rows are drawn again: only when focus has fallen to the document — the row it
 * was in went, or the control was redrawn — it goes to `selector` in the row `rowFocusSuccessor` names. Focus the reader
 * has moved anywhere else is left where it is. Returns the element focused, or null.
 */
export function refocusRow(root: ParentNode, place: RowPlace, selector: string): HTMLElement | null {
  const active = document.activeElement;
  if (active !== null && active !== document.body) return null;
  const rows = shownRows(root);
  const id = rowFocusSuccessor(rows.map((row) => row.dataset.rowId ?? ''), place);
  const row = id === null ? undefined : rows.find((candidate) => candidate.dataset.rowId === id);
  const target = row?.querySelector<HTMLElement>(selector) ?? null;
  target?.focus();
  return target;
}
